import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { History, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { tabMenuKeyToTranslationKey } from "@/lib/utils";
import {
  ImportHistoryEntry,
  useCancelImport,
  useImportHistory,
} from "@/api/import";

/**
 * Historique des imports Excel de l'instance.
 *
 * Répond au manque « pas de liste des imports passés » : sur la page d'accueil
 * de l'import, chaque ligne renvoie au rapport du job (deep-link `?job&step`),
 * réutilisant l'étape Rapport telle quelle — rien n'est recalculé côté client.
 */
const PAGE_SIZE = 10;

/** Couleur et libellé par défaut d'un statut de job. */
const STATUS_TONE: Record<string, string> = {
  completed: "bg-green-100 text-green-800",
  completedWithErrors: "bg-amber-100 text-amber-800",
  failed: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-600",
  expired: "bg-gray-100 text-gray-500",
};

const STATUS_LABELS: Record<string, string> = {
  completed: "Terminé",
  completedWithErrors: "Terminé avec erreurs",
  failed: "Échec",
  cancelled: "Annulé",
  expired: "Expiré",
  ready: "Prêt",
  awaitingMapping: "À compléter",
  awaitingDecisions: "Liaisons à confirmer",
  queued: "En attente",
  committing: "En cours",
  uploaded: "Déposé",
  analyzing: "Analyse",
};

/**
 * Statuts « brouillon » annulables depuis l'historique : une analyse lancée sans
 * aller au bout occupe un slot de la limite « 2 imports simultanés » tant qu'elle
 * n'est pas annulée. On n'offre PAS l'annulation sur `queued`/`committing` (une
 * écriture peut être en cours).
 */
const CANCELLABLE = new Set([
  "uploaded",
  "analyzing",
  "awaitingMapping",
  "awaitingDecisions",
  "ready",
]);

/**
 * Étape du wizard vers laquelle ouvrir un import, selon son statut : on renvoie
 * chacun à l'endroit où il en est resté, pas systématiquement au rapport.
 */
function stepForEntry(entry: ImportHistoryEntry): string {
  if (entry.undoneAt) return "report";
  switch (entry.status) {
    case "awaitingMapping":
    case "uploaded":
    case "analyzing":
      return "review";
    case "awaitingDecisions":
      return "relations";
    case "ready":
      return "confirm";
    case "queued":
    case "committing":
      return "running";
    default: // completed, completedWithErrors, failed, cancelled, expired
      return "report";
  }
}

const StatusBadge = ({ entry }: { entry: ImportHistoryEntry }) => {
  const vt = useVisibleT();
  if (entry.undoneAt) {
    return (
      <Badge variant="secondary" className="font-normal text-gray-600">
        {vt("bo.import.history.undone", "Annulé (fiches supprimées)")}
      </Badge>
    );
  }
  const tone = STATUS_TONE[entry.status] ?? "bg-gray-100 text-gray-600";
  return (
    <Badge className={`font-normal ${tone}`}>
      {vt(`bo.import.status.${entry.status}`, STATUS_LABELS[entry.status] ?? entry.status)}
    </Badge>
  );
};

export const ImportHistory = () => {
  const vt = useVisibleT();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const cancelImport = useCancelImport();
  const [page, setPage] = useState(0);
  const { data } = useImportHistory({ take: PAGE_SIZE, skip: page * PAGE_SIZE });

  const cancel = (jobId: string) =>
    cancelImport.mutate(jobId, {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ["import", "history"] }),
    });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  if (total === 0) return null;

  return (
    <section className="flex flex-col gap-3">
      <h3 className="flex items-center gap-2 text-base font-semibold">
        <History className="w-4 h-4" aria-hidden />
        {vt("bo.import.history.title", "Vos imports récents")}
      </h3>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{vt("bo.import.history.file", "Fichier")}</TableHead>
            <TableHead className="w-[14%]">
              {vt("bo.import.history.type", "Type")}
            </TableHead>
            <TableHead className="w-[16%]">
              {vt("bo.import.history.date", "Date")}
            </TableHead>
            <TableHead className="w-[20%]">
              {vt("bo.import.history.result", "Résultat")}
            </TableHead>
            <TableHead className="w-[16%]">
              {vt("bo.import.history.status", "Statut")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((entry) => (
            <TableRow key={entry.jobId}>
              <TableCell className="align-top">
                <Link
                  to={`/import?job=${entry.jobId}&table=${entry.table}&step=${stepForEntry(entry)}`}
                  className="font-medium text-blue-700 hover:underline"
                >
                  {entry.fileName || vt("bo.import.history.noName", "(sans nom)")}
                </Link>
                {entry.author && (
                  <span className="block text-xs text-gray-500">{entry.author}</span>
                )}
              </TableCell>
              <TableCell className="align-top text-sm">
                {t(tabMenuKeyToTranslationKey(entry.table))}
              </TableCell>
              <TableCell className="align-top text-sm tabular-nums text-gray-600">
                {new Date(entry.createdAt).toLocaleDateString("fr-FR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </TableCell>
              <TableCell className="align-top text-sm text-gray-600">
                {entry.undoneAt ? (
                  <span className="text-gray-400">—</span>
                ) : (
                  <span className="tabular-nums">
                    {entry.createdCount > 0 &&
                      `${entry.createdCount} ${vt("bo.import.history.created", "créées")}`}
                    {entry.updatedCount > 0 &&
                      ` · ${entry.updatedCount} ${vt("bo.import.history.updated", "maj")}`}
                    {entry.errorCount > 0 && (
                      <span className="text-red-700">
                        {" · "}
                        {entry.errorCount} {vt("bo.import.history.errors", "erreurs")}
                      </span>
                    )}
                  </span>
                )}
              </TableCell>
              <TableCell className="align-top">
                <div className="flex flex-col items-start gap-1">
                  <StatusBadge entry={entry} />
                  {CANCELLABLE.has(entry.status) && !entry.undoneAt && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-1.5 text-xs text-gray-500 hover:text-red-700"
                      disabled={cancelImport.isPending}
                      onClick={() => cancel(entry.jobId)}
                    >
                      <X className="w-3 h-3 mr-1" aria-hidden />
                      {vt("bo.import.history.cancel", "Abandonner")}
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500 tabular-nums">
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)}{" "}
            {vt("bo.import.decisions.of", "sur")} {total}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page === 0}
              onClick={() => setPage((current) => current - 1)}
            >
              {vt("bo.import.previous", "Précédent")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={(page + 1) * PAGE_SIZE >= total}
              onClick={() => setPage((current) => current + 1)}
            >
              {vt("bo.import.next", "Suivant")}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};
